import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { ref, onValue, off, push, set, update, get } from "firebase/database";

import { realtimeDb } from "../helper/firebase";
// import { API_URL } from "..";
const API_URL = "https://api.test.frontforcecrm.com";

const ADMIN_ID = "1111";
const ADMIN_JOB_CHAT_PATH = "adminMessageChat";
const ADMIN_JOB_CHAT_META_PATH = "adminMessageChatMeta";
const CHAT_UPLOAD_URL = `${API_URL}/api/chat/upload-attachments`;

const isDeletedForAdmin = (message) => {
  return (
    message?.deletedFor?.[ADMIN_ID] === true ||
    message?.deletedFor?.[String(ADMIN_ID)] === true
  );
};

const isDeletedForEveryone = (message) => {
  return message?.deletedForEveryone === true;
};

const isThreadClosedForAdmin = (meta) => {
  return (
    meta?.closedFor?.[ADMIN_ID] === true ||
    meta?.closedFor?.[String(ADMIN_ID)] === true ||
    meta?.closedForAdmin === true
  );
};

const isThreadDeletedForAdmin = (meta) => {
  return (
    meta?.deletedFor?.[ADMIN_ID] === true ||
    meta?.deletedFor?.[String(ADMIN_ID)] === true ||
    meta?.deletedForAdmin === true
  );
};

const getVisibleText = (message) => {
  if (isDeletedForEveryone(message)) return "This message was deleted";
  return message?.text || "";
};

const formatTime = (dateValue) => {
  if (!dateValue) return "";

  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDate = (dateValue) => {
  if (!dateValue) return "";

  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
  });
};

const getInitials = (name = "") => {
  const words = String(name).trim().split(" ").filter(Boolean);

  if (words.length === 0) return "E";

  return words
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();
};

const getMessageSenderId = (message) => {
  return String(
    message?.user?._id ||
      message?.sender_id ||
      message?.senderId ||
      ""
  );
};

const getMessageReceiverId = (message) => {
  return String(
    message?.receiver?._id ||
      message?.receiver_id ||
      message?.receiverId ||
      ""
  );
};

const getFilePreviewUrl = (file) => {
  if (!file) return "";
  return URL.createObjectURL(file);
};

const getElectricianIdFromMessages = (messages = []) => {
  const incoming = messages.find((message) => {
    const senderId = getMessageSenderId(message);
    const senderRole = String(message?.user?.role || "").toLowerCase();

    return (
      senderId &&
      senderId !== String(ADMIN_ID) &&
      senderRole === "electrician"
    );
  });

  if (incoming) return getMessageSenderId(incoming);

  const outgoing = messages.find((message) => {
    const receiverId = getMessageReceiverId(message);
    const receiverRole = String(message?.receiver?.role || "").toLowerCase();

    return receiverId && receiverRole === "electrician";
  });

  if (outgoing) return getMessageReceiverId(outgoing);

  const anyNonAdminSender = messages.find((message) => {
    const senderId = getMessageSenderId(message);
    return senderId && senderId !== String(ADMIN_ID);
  });

  if (anyNonAdminSender) return getMessageSenderId(anyNonAdminSender);

  const anyNonAdminReceiver = messages.find((message) => {
    const receiverId = getMessageReceiverId(message);
    return receiverId && receiverId !== String(ADMIN_ID);
  });

  if (anyNonAdminReceiver) return getMessageReceiverId(anyNonAdminReceiver);

  return "";
};

const normalizeElectricianList = (data) => {
  const list = Array.isArray(data?.data)
    ? data.data
    : Array.isArray(data?.electricians)
    ? data.electricians
    : Array.isArray(data)
    ? data
    : [];

  return list.map((item) => ({
    ...item,
    id: item.id || item.value || item._id,
    name:
      item.name ||
      item.fullName ||
      item.username ||
      item.email ||
      `Electrician #${item.id || item.value || item._id}`,
  }));
};

const getElectricianNameById = (electricianMap, electricianId) => {
  if (!electricianId) return "Electrician";

  return (
    electricianMap[String(electricianId)]?.name ||
    `Electrician #${electricianId}`
  );
};

const getLastMessagePreview = (message) => {
  if (!message) return "No message yet";
  if (isDeletedForEveryone(message)) return "This message was deleted";
  if (message?.text) return message.text;
  if (message?.image) return "Image message";

  if (Array.isArray(message?.attachments) && message.attachments.length > 0) {
    return message.attachments.length > 1
      ? `${message.attachments.length} images`
      : "Image attachment";
  }

  return "New message";
};

const isIncomingUnreadForAdmin = (message) => {
  const senderId = getMessageSenderId(message);
  const readByAdmin = Boolean(message?.readBy?.[String(ADMIN_ID)]);

  const isIncomingFromElectrician = senderId && senderId !== String(ADMIN_ID);

  return (
    isIncomingFromElectrician &&
    !readByAdmin &&
    message?.status !== "deleted" &&
    !isDeletedForAdmin(message) &&
    !isDeletedForEveryone(message)
  );
};

const buildThreadFromJobMessages = (jobId, rawMessages, meta = {}) => {
  const messageList = Object.entries(rawMessages || {})
    .map(([firebaseKey, value]) => ({
      firebaseKey,
      ...value,
    }))
    .filter((message) => !isDeletedForAdmin(message))
    .sort((a, b) => {
      const aTime = new Date(a?.createdAt || 0).getTime();
      const bTime = new Date(b?.createdAt || 0).getTime();
      return aTime - bTime;
    });

  const lastMessage = messageList[messageList.length - 1];
  const electricianId = getElectricianIdFromMessages(messageList);
  const unreadCount = messageList.filter(isIncomingUnreadForAdmin).length;

  return {
    id: String(jobId),
    job_id: String(jobId),
    electrician_id: electricianId,
    lastMessage: getLastMessagePreview(lastMessage),
    lastMessageBy: getMessageSenderId(lastMessage),
    lastMessageAt: lastMessage?.createdAt || "",
    unreadCount,
    totalMessages: messageList.length,
    closedForAdmin: isThreadClosedForAdmin(meta),
    deletedForAdmin: isThreadDeletedForAdmin(meta),
    closedAt: meta?.closedAt || "",
    closedBy: meta?.closedBy || "",
    deletedAt: meta?.deletedAt || "",
    deletedBy: meta?.deletedBy || "",
  };
};

const ChatMessageLayer = () => {
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [messages, setMessages] = useState([]);

  const [rawChats, setRawChats] = useState({});
  const [chatMeta, setChatMeta] = useState({});
  const [listMode, setListMode] = useState("active");

  const [electricianMap, setElectricianMap] = useState({});
  const [chatSearchText, setChatSearchText] = useState("");
  const [messageText, setMessageText] = useState("");
  const [sending, setSending] = useState(false);

  const [pendingAttachments, setPendingAttachments] = useState([]);

  const [confirmModal, setConfirmModal] = useState({
    visible: false,
    type: "",
    thread: null,
    loading: false,
  });

  const messageListRef = useRef(null);
  const galleryInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const uploadXhrRefs = useRef({});

  useEffect(() => {
    fetchElectricians();

    return () => {
      Object.values(uploadXhrRefs.current || {}).forEach((xhr) => {
        try {
          xhr?.abort?.();
        } catch (error) {
          console.error("Upload abort cleanup error:", error);
        }
      });

      uploadXhrRefs.current = {};
    };
  }, []);

  useEffect(() => {
    const chatsRef = ref(realtimeDb, ADMIN_JOB_CHAT_PATH);

    const unsubscribe = onValue(chatsRef, (snapshot) => {
      setRawChats(snapshot.exists() ? snapshot.val() || {} : {});
    });

    return () => {
      off(chatsRef, "value", unsubscribe);
    };
  }, []);

  useEffect(() => {
    const metaRef = ref(realtimeDb, ADMIN_JOB_CHAT_META_PATH);

    const unsubscribe = onValue(metaRef, (snapshot) => {
      setChatMeta(snapshot.exists() ? snapshot.val() || {} : {});
    });

    return () => {
      off(metaRef, "value", unsubscribe);
    };
  }, []);

  useEffect(() => {
    const nextThreads = Object.entries(rawChats || {})
      .map(([jobId, rawMessages]) =>
        buildThreadFromJobMessages(jobId, rawMessages, chatMeta?.[jobId] || {})
      )
      .filter((thread) => thread.totalMessages > 0)
      .filter((thread) => !thread.deletedForAdmin)
      .sort((a, b) => {
        const aUnread = Number(a.unreadCount || 0);
        const bUnread = Number(b.unreadCount || 0);

        if (aUnread !== bUnread) return bUnread - aUnread;

        const aTime = new Date(a.lastMessageAt || 0).getTime();
        const bTime = new Date(b.lastMessageAt || 0).getTime();

        return bTime - aTime;
      });

    setThreads(nextThreads);

    setSelectedThread((current) => {
      if (!current?.job_id) return null;

      const updatedCurrent = nextThreads.find(
        (item) => String(item.job_id) === String(current.job_id)
      );

      if (!updatedCurrent || updatedCurrent.deletedForAdmin) {
        return null;
      }

      return updatedCurrent;
    });
  }, [rawChats, chatMeta]);

  useEffect(() => {
    if (!selectedThread?.job_id) {
      setMessages([]);
      return;
    }

    const jobId = String(selectedThread.job_id);
    const messagesRef = ref(realtimeDb, `${ADMIN_JOB_CHAT_PATH}/${jobId}`);

    const unsubscribe = onValue(messagesRef, (snapshot) => {
      if (!snapshot.exists()) {
        setMessages([]);
        return;
      }

      const nextMessages = Object.entries(snapshot.val())
        .map(([firebaseKey, value]) => ({
          firebaseKey,
          ...value,
        }))
        .filter((message) => !isDeletedForAdmin(message))
        .sort((a, b) => {
          const aTime = new Date(a?.createdAt || 0).getTime();
          const bTime = new Date(b?.createdAt || 0).getTime();
          return aTime - bTime;
        });

      setMessages(nextMessages);

      setTimeout(() => {
        if (messageListRef.current) {
          messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
        }
      }, 100);
    });

    return () => {
      off(messagesRef, "value", unsubscribe);
    };
  }, [selectedThread?.job_id]);

  const fetchElectricians = async () => {
    try {
      const response = await fetch(`${API_URL}/api/user/fetch-electrician`, {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      });

      const data = await response.json();

      if (!response.ok) {
        console.error("Fetch electrician API error:", data);
        setElectricianMap({});
        return;
      }

      const list = normalizeElectricianList(data);
      const map = {};

      list.forEach((electrician) => {
        if (electrician?.id) {
          map[String(electrician.id)] = electrician;
        }
      });

      setElectricianMap(map);
    } catch (error) {
      console.error("Fetch electricians error:", error);
      setElectricianMap({});
    }
  };

  const markThreadAsRead = async (jobId) => {
    if (!jobId) return;

    try {
      const jobChatRef = ref(realtimeDb, `${ADMIN_JOB_CHAT_PATH}/${jobId}`);
      const snapshot = await get(jobChatRef);

      if (!snapshot.exists()) return;

      const rawMessages = snapshot.val();
      const readUpdates = {};
      const now = new Date().toISOString();

      Object.entries(rawMessages || {}).forEach(([firebaseKey, message]) => {
        if (isIncomingUnreadForAdmin(message)) {
          readUpdates[`${firebaseKey}/readBy/${String(ADMIN_ID)}`] = true;
          readUpdates[`${firebaseKey}/readAt`] = now;
          readUpdates[`${firebaseKey}/status`] = "read";
        }
      });

      if (Object.keys(readUpdates).length > 0) {
        await update(jobChatRef, readUpdates);
      }
    } catch (error) {
      console.error("Mark thread read error:", error);
    }
  };

  const handleSelectThread = async (thread) => {
    setSelectedThread(thread);
    setPendingAttachments([]);
    setMessageText("");
    await markThreadAsRead(thread.job_id);
  };

  const openThreadConfirmModal = (type, thread) => {
    setConfirmModal({
      visible: true,
      type,
      thread,
      loading: false,
    });
  };

  const closeThreadConfirmModal = () => {
    if (confirmModal.loading) return;

    setConfirmModal({
      visible: false,
      type: "",
      thread: null,
      loading: false,
    });
  };

  const confirmDeleteOrCloseThread = async () => {
    const actionType = confirmModal.type;
    const thread = confirmModal.thread;

    if (!thread?.job_id || !actionType) return;

    const jobId = String(thread.job_id);
    const now = new Date().toISOString();

    try {
      setConfirmModal((prev) => ({
        ...prev,
        loading: true,
      }));

      const metaRef = ref(realtimeDb, `${ADMIN_JOB_CHAT_META_PATH}/${jobId}`);

      if (actionType === "delete") {
        await update(metaRef, {
          job_id: jobId,
          [`deletedFor/${ADMIN_ID}`]: true,
          deletedForAdmin: true,
          deletedAt: now,
          deletedBy: String(ADMIN_ID),
          updatedAt: now,
        });

        if (String(selectedThread?.job_id) === jobId) {
          setSelectedThread(null);
          setMessages([]);
        }
      }

      if (actionType === "close") {
        await update(metaRef, {
          job_id: jobId,
          [`closedFor/${ADMIN_ID}`]: true,
          closedForAdmin: true,
          closedAt: now,
          closedBy: String(ADMIN_ID),
          updatedAt: now,
        });

        if (String(selectedThread?.job_id) === jobId) {
          setSelectedThread((prev) =>
            prev
              ? {
                  ...prev,
                  closedForAdmin: true,
                  closedAt: now,
                  closedBy: String(ADMIN_ID),
                }
              : prev
          );
        }

        setListMode("closed");
      }

      setConfirmModal({
        visible: false,
        type: "",
        thread: null,
        loading: false,
      });
    } catch (error) {
      console.error("Thread action error:", error);

      setConfirmModal((prev) => ({
        ...prev,
        loading: false,
      }));

      alert(
        actionType === "delete"
          ? "Chat delete nahi ho saki."
          : "Chat close nahi ho saki."
      );
    }
  };

  const addFilesToPending = (fileList) => {
    const files = Array.from(fileList || []).filter((file) =>
      String(file?.type || "").startsWith("image/")
    );

    if (files.length === 0) return;

    const nextItems = files.map((file) => ({
      localId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      file,
      previewUrl: getFilePreviewUrl(file),
      name: file.name || `chat-image-${Date.now()}.jpg`,
      type: file.type || "image/jpeg",
      status: "queued",
      progress: 0,
      serverUrl: "",
      error: "",
    }));

    setPendingAttachments((prev) => [...prev, ...nextItems]);
  };

  const handleGalleryChange = (event) => {
    addFilesToPending(event.target.files);
    event.target.value = "";
  };

  const handleCameraChange = (event) => {
    addFilesToPending(event.target.files);
    event.target.value = "";
  };

  const cancelAttachmentUpload = (localId) => {
    try {
      uploadXhrRefs.current?.[localId]?.abort?.();
      delete uploadXhrRefs.current[localId];

      setPendingAttachments((prev) =>
        prev.map((file) =>
          file.localId === localId
            ? {
                ...file,
                status: "cancelled",
                progress: 0,
                error: "Cancelled",
              }
            : file
        )
      );
    } catch (error) {
      console.error("Cancel upload error:", error);
    }
  };

  const removePendingAttachment = (localId) => {
    const item = pendingAttachments.find((file) => file.localId === localId);

    if (item?.status === "uploading") {
      const confirmCancel = window.confirm(
        "Ye image upload ho rahi hai. Cancel karni hai?"
      );

      if (confirmCancel) {
        cancelAttachmentUpload(localId);
      }

      return;
    }

    if (item?.previewUrl) {
      URL.revokeObjectURL(item.previewUrl);
    }

    setPendingAttachments((prev) =>
      prev.filter((file) => file.localId !== localId)
    );
  };

  const uploadSingleAttachment = (attachment, jobId, receiverId) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      uploadXhrRefs.current[attachment.localId] = xhr;

      const formData = new FormData();

      formData.append("files", attachment.file);
      formData.append("chat_id", String(jobId));
      formData.append("job_id", String(jobId));
      formData.append("sender_id", String(ADMIN_ID));
      formData.append("receiver_id", String(receiverId));
      formData.append("type", "admin_job_chat");

      xhr.open("POST", CHAT_UPLOAD_URL);

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);

          setPendingAttachments((prev) =>
            prev.map((file) =>
              file.localId === attachment.localId
                ? {
                    ...file,
                    status: "uploading",
                    progress,
                  }
                : file
            )
          );
        }
      };

      xhr.onload = () => {
        delete uploadXhrRefs.current[attachment.localId];

        try {
          const response = JSON.parse(xhr.responseText || "{}");

          if (xhr.status >= 200 && xhr.status < 300 && response?.success) {
            const uploadedFile = response.files?.[0];

            setPendingAttachments((prev) =>
              prev.map((file) =>
                file.localId === attachment.localId
                  ? {
                      ...file,
                      status: "uploaded",
                      progress: 100,
                      serverUrl: uploadedFile?.url || "",
                    }
                  : file
              )
            );

            resolve(uploadedFile);
            return;
          }

          setPendingAttachments((prev) =>
            prev.map((file) =>
              file.localId === attachment.localId
                ? {
                    ...file,
                    status: "error",
                    error: response?.message || "Upload failed",
                  }
                : file
            )
          );

          reject(new Error(response?.message || "Upload failed"));
        } catch (error) {
          setPendingAttachments((prev) =>
            prev.map((file) =>
              file.localId === attachment.localId
                ? {
                    ...file,
                    status: "error",
                    error: "Invalid upload response",
                  }
                : file
            )
          );

          reject(error);
        }
      };

      xhr.onerror = () => {
        delete uploadXhrRefs.current[attachment.localId];

        setPendingAttachments((prev) =>
          prev.map((file) =>
            file.localId === attachment.localId
              ? {
                  ...file,
                  status: "error",
                  error: "Network error",
                }
              : file
          )
        );

        reject(new Error("Network error"));
      };

      xhr.onabort = () => {
        delete uploadXhrRefs.current[attachment.localId];

        setPendingAttachments((prev) =>
          prev.map((file) =>
            file.localId === attachment.localId
              ? {
                  ...file,
                  status: "cancelled",
                  progress: 0,
                  error: "Cancelled",
                }
              : file
          )
        );

        reject(new Error("Upload cancelled"));
      };

      setPendingAttachments((prev) =>
        prev.map((file) =>
          file.localId === attachment.localId
            ? {
                ...file,
                status: "uploading",
                progress: 0,
                error: "",
              }
            : file
        )
      );

      xhr.send(formData);
    });
  };

  const uploadPendingAttachments = async (filesSnapshot, jobId, receiverId) => {
    const filesToUpload = filesSnapshot.filter((file) => {
      return file.status === "queued" || file.status === "error";
    });

    const alreadyUploaded = filesSnapshot
      .filter((file) => file.status === "uploaded" && file.serverUrl)
      .map((file) => ({
        url: file.serverUrl,
        name: file.name,
        type: file.type,
      }));

    if (filesToUpload.length === 0) {
      return alreadyUploaded;
    }

    const uploadedFiles = [];

    for (const attachment of filesToUpload) {
      const uploaded = await uploadSingleAttachment(
        attachment,
        jobId,
        receiverId
      );

      uploadedFiles.push({
        url: uploaded?.url,
        name: uploaded?.originalName || uploaded?.filename || attachment.name,
        type: uploaded?.mimeType || attachment.type,
        filename: uploaded?.filename,
      });
    }

    return [...alreadyUploaded, ...uploadedFiles].filter((file) => file.url);
  };

  const filteredThreads = useMemo(() => {
    const keyword = chatSearchText.trim().toLowerCase();

    const modeFilteredThreads = threads.filter((thread) => {
      if (listMode === "closed") return thread.closedForAdmin;
      return !thread.closedForAdmin;
    });

    if (!keyword) return modeFilteredThreads;

    return modeFilteredThreads.filter((thread) => {
      const jobId = String(thread.job_id || "").toLowerCase();
      const electricianId = String(thread.electrician_id || "").toLowerCase();
      const electricianName = getElectricianNameById(
        electricianMap,
        thread.electrician_id
      ).toLowerCase();
      const lastMessage = String(thread.lastMessage || "").toLowerCase();

      return (
        jobId.includes(keyword) ||
        electricianId.includes(keyword) ||
        electricianName.includes(keyword) ||
        lastMessage.includes(keyword)
      );
    });
  }, [threads, chatSearchText, electricianMap, listMode]);

  const activeThreadCount = useMemo(() => {
    return threads.filter((thread) => !thread.closedForAdmin).length;
  }, [threads]);

  const closedThreadCount = useMemo(() => {
    return threads.filter((thread) => thread.closedForAdmin).length;
  }, [threads]);

  const totalUnread = useMemo(() => {
    return threads
      .filter((thread) => !thread.closedForAdmin)
      .reduce((sum, thread) => {
        return sum + Number(thread?.unreadCount || 0);
      }, 0);
  }, [threads]);

  const selectedElectricianName = selectedThread
    ? getElectricianNameById(electricianMap, selectedThread.electrician_id)
    : "";

  const selectedThreadIsClosed = Boolean(selectedThread?.closedForAdmin);

  const sendMessage = async (event) => {
    event.preventDefault();

    if (selectedThreadIsClosed) {
      alert("Ye chat closed hai. Closed chat mein new message send nahi ho sakta.");
      return;
    }

    const cleanMessage = messageText.trim();
    const validPendingFiles = pendingAttachments.filter((file) =>
      ["queued", "uploaded", "error"].includes(file.status)
    );

    const hasFiles = validPendingFiles.length > 0;

    if ((!cleanMessage && !hasFiles) || !selectedThread?.job_id || sending) {
      return;
    }

    const receiverId = selectedThread.electrician_id;

    if (!receiverId) {
      alert(
        "Electrician id missing hai. Electrician se pehle message aana zaroori hai ya job response mein electrician id attach honi chahiye."
      );
      return;
    }

    setSending(true);

    const jobId = String(selectedThread.job_id);
    const now = new Date().toISOString();

    try {
      let uploadedAttachments = [];

      if (hasFiles) {
        uploadedAttachments = await uploadPendingAttachments(
          validPendingFiles,
          jobId,
          receiverId
        );
      }

      const firstImage = uploadedAttachments.find((file) =>
        String(file.type || "").startsWith("image/")
      );

      const messagePreview =
        cleanMessage ||
        (uploadedAttachments.length > 1
          ? `${uploadedAttachments.length} images`
          : uploadedAttachments.length === 1
          ? "Image attachment"
          : "");

      const messageData = {
        _id: Date.now().toString(),
        text: cleanMessage,
        createdAt: now,
        user: {
          _id: String(ADMIN_ID),
          role: "admin",
          name: "Admin",
        },
        receiver: {
          _id: String(receiverId),
          role: "electrician",
        },
        job_id: jobId,
        chat_type: "admin_job_chat",
        type: "admin_job_chat",
        status: "sent",
        readBy: {
          [String(ADMIN_ID)]: true,
        },
        edited: false,
        deletedFor: {},
        deletedForEveryone: false,
        attachments: uploadedAttachments,
        image: firstImage?.url || "",
      };

      const messageRef = push(ref(realtimeDb, `${ADMIN_JOB_CHAT_PATH}/${jobId}`));

      await set(messageRef, messageData);

      setMessageText("");
      setPendingAttachments([]);

      try {
        const response = await fetch(
          `${API_URL}/api/notification/send-notification`,
          {
            method: "POST",
            headers: {
              Accept: "*/*",
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              title: "New Message Received From Admin",
              receiver_id: String(receiverId),
              body: `Admin sent you ${
                messagePreview || "a message"
              } for Job #${jobId}`,
              description: `Admin sent you ${
                messagePreview || "a message"
              } for Job #${jobId}`,
              type: "admin_job_chat",
              job_id: jobId,
            }),
          }
        );

        if (!response.ok) {
          console.error(
            "Admin job chat notification error:",
            await response.text()
          );
        }
      } catch (notificationError) {
        console.error("Admin job chat notification exception:", notificationError);
      }

      setTimeout(async () => {
        await update(messageRef, {
          status: "delivered",
        });
      }, 800);
    } catch (error) {
      console.error("Admin job message send error:", error);

      if (String(error?.message || "").toLowerCase().includes("cancel")) {
        alert("Upload cancel ho gayi.");
      } else {
        alert("Message send nahi ho saka. Console check karein.");
      }
    } finally {
      setSending(false);
    }
  };

  const editMessage = async (message) => {
    if (!selectedThread?.job_id || !message?.firebaseKey) return;

    if (selectedThreadIsClosed) {
      alert("Closed chat mein message edit nahi ho sakta.");
      return;
    }

    if (String(message?.user?._id) !== String(ADMIN_ID)) {
      alert("Aap sirf apna message edit kar sakte hain.");
      return;
    }

    if (isDeletedForEveryone(message)) {
      alert("Deleted message edit nahi ho sakta.");
      return;
    }

    const currentText = message.text || "";
    const newText = window.prompt("Edit message", currentText);

    if (newText === null) return;

    const cleanText = newText.trim();

    if (!cleanText) {
      alert("Message empty nahi ho sakta.");
      return;
    }

    try {
      await update(
        ref(
          realtimeDb,
          `${ADMIN_JOB_CHAT_PATH}/${selectedThread.job_id}/${message.firebaseKey}`
        ),
        {
          text: cleanText,
          edited: true,
          editedAt: new Date().toISOString(),
        }
      );
    } catch (error) {
      console.error("Edit message error:", error);
      alert("Message edit nahi ho saka.");
    }
  };

  const deleteMessageForMe = async (message) => {
    if (!selectedThread?.job_id || !message?.firebaseKey) return;

    const confirmDelete = window.confirm("Delete this message for admin only?");

    if (!confirmDelete) return;

    try {
      await update(
        ref(
          realtimeDb,
          `${ADMIN_JOB_CHAT_PATH}/${selectedThread.job_id}/${message.firebaseKey}`
        ),
        {
          [`deletedFor/${ADMIN_ID}`]: true,
          deletedForAdminAt: new Date().toISOString(),
        }
      );
    } catch (error) {
      console.error("Delete for me error:", error);
      alert("Message delete nahi ho saka.");
    }
  };

  const deleteMessageForEveryone = async (message) => {
    if (!selectedThread?.job_id || !message?.firebaseKey) return;

    if (selectedThreadIsClosed) {
      alert("Closed chat mein message delete for everyone nahi ho sakta.");
      return;
    }

    if (String(message?.user?._id) !== String(ADMIN_ID)) {
      alert("Aap sirf apna message everyone ke liye delete kar sakte hain.");
      return;
    }

    const confirmDelete = window.confirm("Delete this message for everyone?");

    if (!confirmDelete) return;

    try {
      await update(
        ref(
          realtimeDb,
          `${ADMIN_JOB_CHAT_PATH}/${selectedThread.job_id}/${message.firebaseKey}`
        ),
        {
          text: "",
          image: "",
          attachments: [],
          deletedForEveryone: true,
          deletedForEveryoneAt: new Date().toISOString(),
          status: "deleted",
        }
      );
    } catch (error) {
      console.error("Delete for everyone error:", error);
      alert("Message delete nahi ho saka.");
    }
  };

  const renderMessageAttachments = (message) => {
    const attachments = Array.isArray(message.attachments)
      ? message.attachments
      : [];

    if (attachments.length === 0 && message.image) {
      return (
        <div className="rw-message-attachments single">
          <a href={message.image} target="_blank" rel="noreferrer">
            <img src={message.image} alt="chat attachment" />
          </a>
        </div>
      );
    }

    if (attachments.length === 0) return null;

    return (
      <div
        className={`rw-message-attachments ${
          attachments.length === 1 ? "single" : ""
        }`}
      >
        {attachments.map((attachment, index) => {
          const imageUrl = attachment.url || attachment.uri || "";

          if (!imageUrl) return null;

          return (
            <a
              key={`${imageUrl}-${index}`}
              href={imageUrl}
              target="_blank"
              rel="noreferrer"
            >
              <img
                src={imageUrl}
                alt={attachment.name || "chat attachment"}
              />
            </a>
          );
        })}
      </div>
    );
  };

  const renderPendingAttachments = () => {
    if (pendingAttachments.length === 0) return null;

    return (
      <div className="rw-pending-attachments">
        {pendingAttachments.map((file) => (
          <div className="rw-pending-card" key={file.localId}>
            <img src={file.previewUrl} alt={file.name} />

            <button
              type="button"
              className="rw-pending-remove"
              onClick={() => removePendingAttachment(file.localId)}
            >
              <Icon icon="mdi:close" />
            </button>

            {file.status === "uploading" ? (
              <button
                type="button"
                className="rw-pending-cancel"
                onClick={() => cancelAttachmentUpload(file.localId)}
              >
                Cancel
              </button>
            ) : null}

            <div className="rw-progress-track">
              <div
                className={`rw-progress-fill ${
                  file.status === "error" || file.status === "cancelled"
                    ? "danger"
                    : ""
                }`}
                style={{ width: `${file.progress || 0}%` }}
              />
            </div>

            <div className="rw-pending-status">
              {file.status === "queued"
                ? "Ready"
                : file.status === "uploading"
                ? `${file.progress || 0}%`
                : file.status === "uploaded"
                ? "Uploaded"
                : file.status === "cancelled"
                ? "Cancelled"
                : "Failed"}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const canSend =
    !selectedThreadIsClosed &&
    (Boolean(messageText.trim()) ||
      pendingAttachments.some((file) =>
        ["queued", "uploaded", "error"].includes(file.status)
      ));

  return (
    <>
      <style>
        {`
          .rw-job-chat-page {
            display: grid;
            grid-template-columns: 390px minmax(0, 1fr);
            gap: 18px;
            height: calc(100vh - 190px);
            min-height: 640px;
          }

          .rw-job-chat-sidebar,
          .rw-job-chat-main {
            background: #ffffff;
            border: 1px solid rgba(15, 23, 42, 0.08);
            border-radius: 24px;
            overflow: hidden;
            box-shadow: 0 18px 45px rgba(15, 23, 42, 0.08);
          }

          .rw-job-chat-sidebar {
            display: flex;
            flex-direction: column;
          }

          .rw-sidebar-head {
            padding: 18px;
            color: #ffffff;
            background:
              radial-gradient(circle at top right, rgba(255,255,255,0.22), transparent 30%),
              linear-gradient(135deg, #db6f2f 0%, #b95018 100%);
          }

          .rw-sidebar-head-top {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
          }

          .rw-sidebar-title {
            margin: 0;
            color: #ffffff;
            font-size: 20px;
            font-weight: 800;
          }

          .rw-sidebar-subtitle {
            margin: 5px 0 0;
            color: rgba(255,255,255,0.82);
            font-size: 12px;
          }

          .rw-total-unread {
            min-width: 34px;
            height: 34px;
            padding: 0 10px;
            border-radius: 999px;
            background: #dc3545;
            color: #ffffff;
            font-weight: 900;
            font-size: 12px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border: 2px solid rgba(255,255,255,0.85);
          }

          .rw-chat-search {
            margin-top: 16px;
            position: relative;
          }

          .rw-chat-search input {
            width: 100%;
            height: 44px;
            border-radius: 999px;
            border: 1px solid rgba(255,255,255,0.35);
            background: rgba(255,255,255,0.16);
            color: #ffffff;
            padding: 0 16px 0 42px;
            outline: none;
          }

          .rw-chat-search input::placeholder {
            color: rgba(255,255,255,0.74);
          }

          .rw-chat-search .icon {
            position: absolute;
            left: 16px;
            top: 50%;
            transform: translateY(-50%);
            color: #ffffff;
            z-index: 1;
          }

          .rw-chat-tabs {
            margin-top: 12px;
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 8px;
          }

          .rw-chat-tab {
            border: 1px solid rgba(255,255,255,0.26);
            background: rgba(255,255,255,0.14);
            color: #ffffff;
            border-radius: 999px;
            padding: 9px 10px;
            font-size: 12px;
            font-weight: 900;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 7px;
          }

          .rw-chat-tab span {
            min-width: 20px;
            height: 20px;
            border-radius: 999px;
            background: rgba(255,255,255,0.22);
            display: inline-flex;
            align-items: center;
            justify-content: center;
            font-size: 11px;
          }

          .rw-chat-tab.active {
            background: #ffffff;
            color: #db6f2f;
          }

          .rw-chat-tab.active span {
            background: #fff0e8;
            color: #db6f2f;
          }

          .rw-job-thread-list {
            flex: 1;
            overflow-y: auto;
            padding: 10px;
            background: #f8fafc;
          }

          .rw-job-thread {
            width: 100%;
            border: 1px solid transparent;
            background: #ffffff;
            border-radius: 18px;
            padding: 13px;
            display: flex;
            align-items: center;
            gap: 12px;
            text-align: left;
            margin-bottom: 9px;
            transition: all 0.18s ease;
            cursor: pointer;
          }

          .rw-job-thread:hover {
            transform: translateY(-1px);
            border-color: rgba(219, 111, 47, 0.25);
            box-shadow: 0 10px 24px rgba(15, 23, 42, 0.08);
          }

          .rw-job-thread.active {
            border-color: rgba(219, 111, 47, 0.34);
            background: #fff7f1;
          }

          .rw-job-thread.unread {
            background: #fff5f3;
            border-color: rgba(220, 53, 69, 0.2);
          }

          .rw-job-thread.closed {
            background: #f8fafc;
            border-color: #e5e7eb;
            opacity: 0.92;
          }

          .rw-avatar {
            width: 48px;
            height: 48px;
            min-width: 48px;
            border-radius: 16px;
            background: #fff0e8;
            color: #db6f2f;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            font-weight: 900;
          }

          .rw-job-thread.unread .rw-avatar {
            background: #dc3545;
            color: #ffffff;
          }

          .rw-thread-content {
            min-width: 0;
            flex: 1;
          }

          .rw-thread-line {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 8px;
          }

          .rw-thread-title {
            margin: 0;
            color: #111827;
            font-size: 12px !important;
            font-weight: 900;
          }

          .rw-thread-time {
            color: #94a3b8;
            font-size: 11px;
            white-space: nowrap;
          }

          .rw-thread-person {
            margin: 3px 0 0;
            color: #64748b;
            font-size: 12px;
            font-weight: 700;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .rw-thread-message {
            margin: 6px 0 0;
            color: #64748b;
            font-size: 13px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .rw-job-thread.unread .rw-thread-message,
          .rw-job-thread.unread .rw-thread-title {
            color: #111827;
            font-weight: 900;
          }

          .rw-unread-badge {
            min-width: 23px;
            height: 23px;
            padding: 0 7px;
            border-radius: 999px;
            background: #dc3545;
            color: #ffffff;
            font-size: 11px;
            font-weight: 900;
            display: inline-flex;
            align-items: center;
            justify-content: center;
          }

          .rw-thread-actions {
            margin-top: 10px;
            display: flex;
            align-items: center;
            gap: 8px;
          }

          .rw-thread-action-btn {
            width: 32px;
            height: 32px;
            border-radius: 999px;
            border: 0;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            transition: all 0.16s ease;
          }

          .rw-thread-action-btn.close {
            background: #fff7ed;
            color: #db6f2f;
          }

          .rw-thread-action-btn.delete {
            background: #fef2f2;
            color: #dc2626;
          }

          .rw-thread-action-btn:hover {
            transform: translateY(-1px);
            box-shadow: 0 8px 18px rgba(15, 23, 42, 0.12);
          }

          .rw-thread-closed-pill {
            border-radius: 999px;
            background: #e5e7eb;
            color: #475569;
            padding: 7px 10px;
            font-size: 11px;
            font-weight: 900;
          }

          .rw-job-chat-main {
            display: flex;
            flex-direction: column;
            background:
              radial-gradient(circle at 15% 10%, rgba(219,111,47,0.08), transparent 30%),
              linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
          }

          .rw-main-header {
            padding: 16px 18px;
            background: #ffffff;
            border-bottom: 1px solid #e5e7eb;
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
          }

          .rw-main-user {
            display: flex;
            align-items: center;
            gap: 12px;
            min-width: 0;
          }

          .rw-main-title {
            margin: 0;
            color: #111827;
            font-size: 17px;
            font-weight: 900;
          }

          .rw-main-subtitle {
            margin: 3px 0 0;
            color: #64748b;
            font-size: 12px;
            font-weight: 700;
          }

          .rw-main-header-actions {
            display: flex;
            align-items: center;
            gap: 8px;
          }

          .rw-closed-chat-pill {
            border-radius: 999px;
            background: #f1f5f9;
            color: #475569;
            border: 1px solid #e2e8f0;
            padding: 8px 12px;
            font-size: 12px;
            font-weight: 900;
            white-space: nowrap;
          }

          .rw-job-pill {
            border-radius: 999px;
            background: #fff0e8;
            color: #db6f2f;
            border: 1px solid rgba(219,111,47,0.18);
            padding: 8px 12px;
            font-size: 12px;
            font-weight: 900;
            white-space: nowrap;
          }

          .rw-message-list {
            flex: 1;
            overflow-y: auto;
            padding: 22px;
          }

          .rw-message {
            display: flex;
            gap: 10px;
            margin-bottom: 16px;
            align-items: flex-end;
          }

          .rw-message.right {
            justify-content: flex-end;
          }

          .rw-message.left {
            justify-content: flex-start;
          }

          .rw-bubble {
            max-width: 68%;
            border-radius: 18px;
            padding: 12px 14px;
            position: relative;
            box-shadow: 0 10px 24px rgba(15, 23, 42, 0.08);
          }

          .rw-message.left .rw-bubble {
            background: #ffffff;
            color: #111827;
            border-bottom-left-radius: 6px;
            border: 1px solid #eef2f7;
          }

          .rw-message.right .rw-bubble {
            background: linear-gradient(135deg, #db6f2f 0%, #b95018 100%);
            color: #ffffff;
            border-bottom-right-radius: 6px;
          }

          .rw-message.right .rw-bubble p,
          .rw-message.right .rw-bubble span {
            color: #ffffff;
          }

          .rw-bubble-text {
            margin: 0 0 8px;
            line-height: 1.55;
            word-break: break-word;
          }

          .rw-message-meta {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
            font-size: 11px;
            opacity: 0.82;
          }

          .rw-message-attachments {
            display: grid;
            grid-template-columns: repeat(2, minmax(110px, 1fr));
            gap: 8px;
            margin-bottom: 10px;
          }

          .rw-message-attachments.single {
            grid-template-columns: 1fr;
          }

          .rw-message-attachments img {
            width: 100%;
            max-width: 240px;
            height: 150px;
            border-radius: 12px;
            object-fit: cover;
            display: block;
            border: 1px solid rgba(255,255,255,0.25);
          }

          .rw-message-box {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 16px;
            background: #ffffff;
            border-top: 1px solid #e5e7eb;
          }

          .rw-message-box.disabled {
            background: #f8fafc;
            opacity: 0.82;
          }

          .rw-message-box input[type="text"] {
            flex: 1;
            height: 48px;
            border: 1px solid #e5e7eb;
            border-radius: 999px;
            padding: 0 18px;
            background: #f9fafb;
            outline: none;
          }

          .rw-message-box input[type="text"]:focus {
            border-color: #db6f2f;
            background: #ffffff;
            box-shadow: 0 0 0 4px rgba(219,111,47,0.08);
          }

          .rw-message-box.disabled input[type="text"] {
            cursor: not-allowed;
            background: #e5e7eb;
          }

          .rw-hidden-file-input {
            display: none;
          }

          .rw-attach-btn {
            width: 44px;
            height: 44px;
            border-radius: 14px;
            border: 1px solid #fee2d3;
            background: #fff7f1;
            color: #db6f2f;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            font-size: 20px;
            transition: all 0.15s ease;
          }

          .rw-attach-btn:hover {
            background: #fff0e8;
            transform: translateY(-1px);
          }

          .rw-attach-btn:disabled {
            opacity: 0.55;
            cursor: not-allowed;
          }

          .rw-pending-attachments {
            display: flex;
            gap: 10px;
            overflow-x: auto;
            padding: 10px 16px;
            background: #ffffff;
            border-top: 1px solid #eef2f7;
          }

          .rw-pending-card {
            width: 108px;
            min-width: 108px;
            border-radius: 14px;
            border: 1px solid #e5e7eb;
            background: #f8fafc;
            padding: 7px;
            position: relative;
          }

          .rw-pending-card img {
            width: 92px;
            height: 82px;
            border-radius: 11px;
            object-fit: cover;
            display: block;
            background: #e5e7eb;
          }

          .rw-pending-remove {
            position: absolute;
            top: 2px;
            right: 2px;
            width: 24px;
            height: 24px;
            border-radius: 999px;
            background: #dc2626;
            color: #ffffff;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border: 0;
          }

          .rw-pending-cancel {
            position: absolute;
            left: 10px;
            right: 10px;
            bottom: 30px;
            height: 22px;
            border-radius: 999px;
            border: 0;
            background: rgba(15, 23, 42, 0.76);
            color: #ffffff;
            font-size: 10px;
            font-weight: 800;
          }

          .rw-progress-track {
            height: 5px;
            background: #e5e7eb;
            border-radius: 999px;
            margin-top: 7px;
            overflow: hidden;
          }

          .rw-progress-fill {
            height: 5px;
            background: #db6f2f;
            border-radius: 999px;
            transition: width 0.18s ease;
          }

          .rw-progress-fill.danger {
            background: #dc2626;
          }

          .rw-pending-status {
            margin-top: 4px;
            font-size: 10px;
            color: #64748b;
            text-align: center;
            font-weight: 800;
          }

          .rw-empty-state {
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-direction: column;
            color: #64748b;
            padding: 24px;
            text-align: center;
          }

          .rw-empty-state svg {
            font-size: 54px;
            margin-bottom: 12px;
            color: #db6f2f;
          }

          .rw-confirm-overlay {
            position: fixed;
            inset: 0;
            background: rgba(15, 23, 42, 0.48);
            z-index: 9999;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 20px;
          }

          .rw-confirm-modal {
            width: 100%;
            max-width: 420px;
            background: #ffffff;
            border-radius: 24px;
            padding: 24px;
            box-shadow: 0 24px 70px rgba(15, 23, 42, 0.28);
            text-align: center;
          }

          .rw-confirm-icon {
            width: 58px;
            height: 58px;
            border-radius: 20px;
            background: #fff0e8;
            color: #db6f2f;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            font-size: 30px;
            margin-bottom: 14px;
          }

          .rw-confirm-modal h5 {
            margin: 0;
            color: #111827;
            font-size: 20px;
            font-weight: 900;
          }

          .rw-confirm-modal p {
            margin: 10px 0 0;
            color: #64748b;
            font-size: 14px;
            line-height: 1.55;
          }

          .rw-confirm-actions {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 10px;
            margin-top: 22px;
          }

          .rw-confirm-cancel,
          .rw-confirm-primary,
          .rw-confirm-danger {
            border: 0;
            border-radius: 999px;
            min-width: 120px;
            height: 42px;
            padding: 0 16px;
            font-weight: 900;
          }

          .rw-confirm-cancel {
            background: #f1f5f9;
            color: #475569;
          }

          .rw-confirm-primary {
            background: #db6f2f;
            color: #ffffff;
          }

          .rw-confirm-danger {
            background: #dc2626;
            color: #ffffff;
          }

          .rw-confirm-cancel:disabled,
          .rw-confirm-primary:disabled,
          .rw-confirm-danger:disabled {
            opacity: 0.65;
            cursor: not-allowed;
          }

          @media (max-width: 991px) {
            .rw-job-chat-page {
              grid-template-columns: 1fr;
              height: auto;
            }

            .rw-job-chat-sidebar,
            .rw-job-chat-main {
              min-height: 560px;
            }

            .rw-bubble {
              max-width: 84%;
            }

            .rw-message-attachments {
              grid-template-columns: 1fr;
            }
          }
        `}
      </style>

      <div className="rw-job-chat-page">
        <aside className="rw-job-chat-sidebar">
          <div className="rw-sidebar-head">
            <div className="rw-sidebar-head-top">
              <div>
                <p className="rw-sidebar-subtitle">
                  Electrician job-wise admin chat inbox
                </p>
              </div>

              {totalUnread > 0 ? (
                <span className="rw-total-unread">
                  {totalUnread > 99 ? "99+" : totalUnread}
                </span>
              ) : null}
            </div>

            <div className="rw-chat-search">
              <span className="icon">
                <Icon icon="iconoir:search" />
              </span>

              <input
                type="text"
                autoComplete="off"
                placeholder="Search by job id, electrician, message..."
                value={chatSearchText}
                onChange={(event) => setChatSearchText(event.target.value)}
              />
            </div>

            <div className="rw-chat-tabs">
              <button
                type="button"
                className={`rw-chat-tab ${listMode === "active" ? "active" : ""}`}
                onClick={() => {
                  setListMode("active");
                  setSelectedThread(null);
                  setMessages([]);
                }}
              >
                Active Chats
                <span>{activeThreadCount}</span>
              </button>

              <button
                type="button"
                className={`rw-chat-tab ${listMode === "closed" ? "active" : ""}`}
                onClick={() => {
                  setListMode("closed");
                  setSelectedThread(null);
                  setMessages([]);
                }}
              >
                Closed Chats
                <span>{closedThreadCount}</span>
              </button>
            </div>
          </div>

          <div className="rw-job-thread-list">
            {filteredThreads.length === 0 ? (
              <div className="p-4 text-center text-secondary-light">
                {listMode === "closed"
                  ? "No closed chat found"
                  : "No active job message found"}
              </div>
            ) : (
              filteredThreads.map((thread) => {
                const isActive =
                  selectedThread?.job_id &&
                  String(selectedThread.job_id) === String(thread.job_id);

                const isUnread = Number(thread.unreadCount || 0) > 0;
                const electricianName = getElectricianNameById(
                  electricianMap,
                  thread.electrician_id
                );

                return (
                  <div
                    key={thread.job_id}
                    role="button"
                    tabIndex={0}
                    className={`rw-job-thread ${isActive ? "active" : ""} ${
                      isUnread ? "unread" : ""
                    } ${thread.closedForAdmin ? "closed" : ""}`}
                    onClick={() => handleSelectThread(thread)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") handleSelectThread(thread);
                    }}
                  >
                    <div className="rw-avatar">
                      {getInitials(electricianName)}
                    </div>

                    <div className="rw-thread-content">
                      <div className="rw-thread-line">
                        <h6 className="rw-thread-title">Job #{thread.job_id}</h6>

                        <span className="rw-thread-time">
                          {thread.lastMessageAt
                            ? formatDate(thread.lastMessageAt)
                            : ""}
                        </span>
                      </div>

                      <p className="rw-thread-person">{electricianName}</p>

                      <div className="rw-thread-line">
                        <p className="rw-thread-message">
                          {thread.lastMessageBy &&
                          String(thread.lastMessageBy) === String(ADMIN_ID)
                            ? "You: "
                            : "Electrician: "}
                          {thread.lastMessage}
                        </p>

                        {isUnread ? (
                          <span className="rw-unread-badge">
                            {thread.unreadCount > 99
                              ? "99+"
                              : thread.unreadCount}
                          </span>
                        ) : null}
                      </div>

                      <div className="rw-thread-actions">
                        {!thread.closedForAdmin ? (
                          <button
                            type="button"
                            className="rw-thread-action-btn close"
                            title="Close chat"
                            onClick={(event) => {
                              event.stopPropagation();
                              openThreadConfirmModal("close", thread);
                            }}
                          >
                            <Icon icon="solar:archive-down-linear" />
                          </button>
                        ) : (
                          <span className="rw-thread-closed-pill">Closed</span>
                        )}

                        <button
                          type="button"
                          className="rw-thread-action-btn delete"
                          title="Delete chat"
                          onClick={(event) => {
                            event.stopPropagation();
                            openThreadConfirmModal("delete", thread);
                          }}
                        >
                          <Icon icon="solar:trash-bin-trash-linear" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </aside>

        <main className="rw-job-chat-main">
          {selectedThread ? (
            <>
              <div className="rw-main-header">
                <div className="rw-main-user">
                  <div className="rw-avatar">
                    {getInitials(selectedElectricianName)}
                  </div>

                  <div className="min-w-0">
                    <h6 className="rw-main-title">{selectedElectricianName}</h6>
                    <p className="rw-main-subtitle">
                      Electrician ID: {selectedThread.electrician_id || "N/A"}
                    </p>
                  </div>
                </div>

                <div className="rw-main-header-actions">
                  {selectedThreadIsClosed ? (
                    <span className="rw-closed-chat-pill">Closed Chat</span>
                  ) : null}

                  <div className="rw-job-pill">Job #{selectedThread.job_id}</div>
                </div>
              </div>

              <div className="rw-message-list" ref={messageListRef}>
                {messages.length === 0 ? (
                  <div className="rw-empty-state">
                    <Icon icon="solar:chat-round-dots-linear" />
                    <h6>No messages yet</h6>
                    <p className="mb-0">
                      Electrician ka job message yahan show hoga.
                    </p>
                  </div>
                ) : (
                  messages.map((message) => {
                    const isAdminMessage =
                      String(message?.user?._id) === String(ADMIN_ID);

                    const isDeletedEveryone = isDeletedForEveryone(message);

                    return (
                      <div
                        key={message.firebaseKey || message._id}
                        className={`rw-message ${
                          isAdminMessage ? "right" : "left"
                        }`}
                      >
                        {!isAdminMessage ? (
                          <div className="rw-avatar">
                            {getInitials(selectedElectricianName)}
                          </div>
                        ) : null}

                        <div className="rw-bubble">
                          {!isDeletedEveryone
                            ? renderMessageAttachments(message)
                            : null}

                          <p
                            className={`rw-bubble-text ${
                              isDeletedEveryone
                                ? "fst-italic text-secondary-light"
                                : ""
                            }`}
                          >
                            {getVisibleText(message)}

                            {message.edited && !isDeletedEveryone ? (
                              <span className="ms-2 text-xs">(edited)</span>
                            ) : null}
                          </p>

                          <div className="rw-message-meta">
                            <span>
                              {formatTime(message.createdAt)}

                              {isAdminMessage ? (
                                <span className="ms-2">
                                  {message.status === "read"
                                    ? "Read"
                                    : message.status === "delivered"
                                    ? "Delivered"
                                    : message.status === "deleted"
                                    ? "Deleted"
                                    : "Sent"}
                                </span>
                              ) : null}
                            </span>

                            <div className="btn-group">
                              <button
                                type="button"
                                className={`${
                                  isAdminMessage
                                    ? "text-white"
                                    : "text-secondary-light"
                                } text-lg`}
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                              >
                                <Icon icon="tabler:dots-vertical" />
                              </button>

                              <ul className="dropdown-menu dropdown-menu-end border">
                                {isAdminMessage &&
                                !isDeletedEveryone &&
                                !selectedThreadIsClosed ? (
                                  <li>
                                    <button
                                      type="button"
                                      className="dropdown-item rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 d-flex align-items-center gap-2"
                                      onClick={() => editMessage(message)}
                                    >
                                      <Icon icon="solar:pen-linear" />
                                      Edit
                                    </button>
                                  </li>
                                ) : null}

                                <li>
                                  <button
                                    type="button"
                                    className="dropdown-item rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 d-flex align-items-center gap-2"
                                    onClick={() => deleteMessageForMe(message)}
                                  >
                                    <Icon icon="solar:trash-bin-trash-linear" />
                                    Delete for me
                                  </button>
                                </li>

                                {isAdminMessage &&
                                !isDeletedEveryone &&
                                !selectedThreadIsClosed ? (
                                  <li>
                                    <button
                                      type="button"
                                      className="dropdown-item rounded text-danger bg-hover-danger-100 d-flex align-items-center gap-2"
                                      onClick={() =>
                                        deleteMessageForEveryone(message)
                                      }
                                    >
                                      <Icon icon="solar:trash-bin-minimalistic-linear" />
                                      Delete for everyone
                                    </button>
                                  </li>
                                ) : null}
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {renderPendingAttachments()}

              <form
                className={`rw-message-box ${
                  selectedThreadIsClosed ? "disabled" : ""
                }`}
                onSubmit={sendMessage}
              >
                <input
                  ref={cameraInputRef}
                  className="rw-hidden-file-input"
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleCameraChange}
                />

                <input
                  ref={galleryInputRef}
                  className="rw-hidden-file-input"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleGalleryChange}
                />

                <button
                  type="button"
                  className="rw-attach-btn"
                  disabled={sending || selectedThreadIsClosed}
                  title="Camera"
                  onClick={() => cameraInputRef.current?.click()}
                >
                  <Icon icon="solar:camera-linear" />
                </button>

                <button
                  type="button"
                  className="rw-attach-btn"
                  disabled={sending || selectedThreadIsClosed}
                  title="Gallery"
                  onClick={() => galleryInputRef.current?.click()}
                >
                  <Icon icon="solar:gallery-linear" />
                </button>

                <input
                  type="text"
                  name="chatMessage"
                  disabled={selectedThreadIsClosed}
                  placeholder={
                    selectedThreadIsClosed
                      ? "This chat is closed"
                      : `Reply for Job #${selectedThread.job_id}`
                  }
                  value={messageText}
                  onChange={(event) => setMessageText(event.target.value)}
                />

                <button
                  type="submit"
                  disabled={sending || !canSend}
                  className="btn btn-sm btn-primary-600 radius-8 d-inline-flex align-items-center gap-1"
                >
                  {sending ? "Sending..." : "Send"}
                  <Icon icon="f7:paperplane" />
                </button>
              </form>
            </>
          ) : (
            <div className="rw-empty-state">
              <Icon icon="solar:chat-round-dots-linear" />
              <h6>
                {listMode === "closed"
                  ? "No closed chat selected"
                  : "No job chat selected"}
              </h6>
            </div>
          )}
        </main>
      </div>

      {confirmModal.visible ? (
        <div className="rw-confirm-overlay">
          <div className="rw-confirm-modal">
            <div className="rw-confirm-icon">
              <Icon
                icon={
                  confirmModal.type === "delete"
                    ? "solar:trash-bin-trash-bold"
                    : "solar:archive-down-bold"
                }
              />
            </div>

            <h5>
              {confirmModal.type === "delete" ? "Delete Chat?" : "Close Chat?"}
            </h5>

            <p>
              {confirmModal.type === "delete"
                ? `Job #${confirmModal.thread?.job_id} chat admin list se delete ho jayegi.`
                : `Job #${confirmModal.thread?.job_id} chat active list se remove ho kar Closed Chats mein chali jayegi.`}
            </p>

            <div className="rw-confirm-actions">
              <button
                type="button"
                className="rw-confirm-cancel"
                disabled={confirmModal.loading}
                onClick={closeThreadConfirmModal}
              >
                Cancel
              </button>

              <button
                type="button"
                className={
                  confirmModal.type === "delete"
                    ? "rw-confirm-danger"
                    : "rw-confirm-primary"
                }
                disabled={confirmModal.loading}
                onClick={confirmDeleteOrCloseThread}
              >
                {confirmModal.loading
                  ? "Please wait..."
                  : confirmModal.type === "delete"
                  ? "Yes, Delete"
                  : "Yes, Close"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default ChatMessageLayer;